import { Download, PlayArrow } from '@mui/icons-material';
import { Button, ButtonGroup } from '@mui/material';
import dayjs from 'dayjs';
import { sortBy, without } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import { Constants } from '../../../../../../Core/Constants';
import { ContractDate, CustomerReportModel, ExpiryStatus, GetMarkToMarketReportRequest, PositionModel, ScenarioMarketPriceModel } from '../../../../../../Generated/Commodity-Management-Api';
import { ModelToRowTransformer } from '../../../../../../Helpers/ModelToRowTransformer';
import { deepMerge } from '../../../../../../Helpers/ObjectHelper';
import { useLoadingState } from '../../../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../../../Redux/ReduxStore';
import { GroupingOption } from '../../../../../../Types/GridRow';
import { StoneXLoading, StoneXRow } from '../../../../../StoneX';
import { StoneXButton, StoneXContractMonthPicker, StoneXDatePicker } from '../../../../../StoneXMui';
import { ReportProps } from '../../ReportsPage';
import PositionReportSettingsModal from './PositionReportSettingsModal';
import PositionReportSummaryDisplay from './PositionReportSummaryDisplay';
import PositionReportTable from './PositionReportTable';
import ScenarioMarketPrices from './ScenarioMarketPrices';
import { PositionReportColumn, PositionReportConfiguration, PositionReportRow, PositionReportUserOverride } from './Types';
import { LoadingState } from '../../../../../../Types';
import { downloadFile } from '../../../../../../Helpers';
import { enqueueSnackbar } from 'notistack';

export function PositionReport(props: ReportProps) {

  const { customerName, customerReportId, customerData, customerSettings } = props;
  
  const customerReport = useApplicationSelector((state: RootState): CustomerReportModel => state.commodityManagement.customerReports.find((x: any) => x.customerReportId === customerReportId)!);

  const [ quoteDate, setQuoteDate ] = useState<Date | null>(props.quoteDate);
  const quoteDateString = useMemo(() => quoteDate ? dayjs(quoteDate).format('YYYY-MM-DD') : null, [quoteDate]);
  const [ budgetStartMonth, setBudgetStartMonth ] = useState<ContractDate | null>(null);
  const [ budgetEndMonth, setBudgetEndMonth ] = useState<ContractDate | null>(null);
  const [ scenarioMarketPrices, setScenarioMarketPrices ] = useState<ScenarioMarketPriceModel[] | undefined>();
  const isScenarioEnabled = useMemo(() => scenarioMarketPrices && scenarioMarketPrices.length > 0, [scenarioMarketPrices]);

  const reportLoadingState = useLoadingState();
  const reportDownloadingState = useLoadingState();
  const reportIsLoading = reportLoadingState.isLoading() || reportDownloadingState.isLoading();

  const [positions, setPositions] = useState<PositionModel[]>([]);
  const [rows, setRows] = useState<PositionReportRow[]>([]);

  const reportConfiguration = useMemo(() => JSON.parse(customerReport.configuration!) as PositionReportConfiguration, [customerReport]);
  const userOverrides = useMemo(() => customerReport.userOverrides ? JSON.parse(customerReport.userOverrides) as PositionReportUserOverride : undefined, [customerReport]);
  const reportSettings = useMemo(() =>  deepMerge(reportConfiguration, userOverrides), [reportConfiguration, userOverrides]);
  const columnsToShow = useMemo(getColumnsToShow, [reportSettings, isScenarioEnabled]);

  const [ groupingOptions, setGroupingOptions ] = useState<GroupingOption[]>([]);

  const [ isConfiguringReport, setIsConfiguringReport ] = useState<boolean>(false);

  function runReport() {

    const request = {
      customerReportId: customerReport.customerReportId,
      expiryStatus: reportConfiguration?.behavior?.expiryStatus ?? ExpiryStatus.Expired,
      budgetStartMonth: budgetStartMonth ? budgetStartMonth.month! + 1 : undefined,
      budgetEndMonth: budgetEndMonth ? budgetEndMonth.month! + 1 : undefined,
      scenarioMarketPrices: scenarioMarketPrices,
    } as GetMarkToMarketReportRequest;

    CommodityManagementApi.getMarkToMarketReport(
      reportLoadingState.setLoadingState,
      quoteDateString!,
      request
    ).then((res) => setPositions(res.data.rows ?? [])
    ).catch(err => { enqueueSnackbar(err.response.data.detail, { variant: 'error' }); });
  }

  function downloadToExcel() {
    
    const request = {
      customerReportId: customerReport.customerReportId,
      expiryStatus: reportConfiguration?.behavior?.expiryStatus ?? ExpiryStatus.Expired,
      budgetStartMonth: budgetStartMonth ? budgetStartMonth.month! + 1 : undefined,
      budgetEndMonth: budgetEndMonth ? budgetEndMonth.month! + 1 : undefined,
      scenarioMarketPrices: scenarioMarketPrices,
    } as GetMarkToMarketReportRequest;

    CommodityManagementApi.getMarkToMarketExcelReport(
      reportDownloadingState.setLoadingState,
      quoteDateString!,
      request,
      {responseType: 'blob', headers: {'ContentType': Constants.MediaType.Excel}}
    ).then(downloadFile);
  }

  function getColumnsToShow() {
    if (isScenarioEnabled) {
      return reportSettings.columns?.active;
    }
    else {
      const scenarioColumns: PositionReportColumn[] = ['ScenarioMarketPrice', 'ScenarioMtmPrice', 'ScenarioMtmValue'];
      return without(reportSettings.columns?.active, ...scenarioColumns);
    }
  }

  function updateReportRows() {
    const rows: PositionReportRow[] = positions.map(ModelToRowTransformer.convertModelToTableRow);

    // Filter out undefined groupings
    const selectedGroupings = [reportSettings?.behavior?.grouping?.[0], reportSettings?.behavior?.grouping?.[1]].filter(x => !!x).map(x => x!);

    // Update the path for each row based on groupings
    ModelToRowTransformer.updatePaths(rows, selectedGroupings);

    // Add positionId to path to make each path unique
    rows.forEach(x => x.path.push(x.positionId?.toString() ?? ''));

    // Sort rows so tree groups display in a meaningful default order per grouping
    const sortedRows = sortBy(rows, selectedGroupings.map(grouping => (row: PositionReportRow) => getGroupingSortKey(row, grouping)));

    setRows(sortedRows);
  }

  function getGroupingSortKey(row: PositionReportRow, grouping: GroupingOption): number | string {
    switch (grouping) {
      case 'BudgetMonth':
        return (row.budgetDate?.year ?? 0) * 12 + (row.budgetDate?.month ?? 0);
      case 'ContractMonth':
        return (row.effectiveContractDate?.year ?? 0) * 12 + (row.effectiveContractDate?.month ?? 0);
      case 'BudgetYear':
        return row.budgetDate?.year ?? 0;
      case 'Commodity':
        return (row.commodity?.commodityName ?? '').toLowerCase();
      case 'Counterparty':
        return (row.counterparty?.counterpartyName ?? '').toLowerCase();
      case 'BudgetLabel':
        return (row.budgetLabel ?? '').toLowerCase();
      case 'BudgetingTier':
        return (row.location?.locationName ?? '').toLowerCase();
      case 'Tiers':
        return (row.locationHierarchy?.map(x => x.locationName).join('/') ?? '').toLowerCase();
      default:
        return '';
    }
  }

  function updateGroupingOptions() {

    const groupingOptions: GroupingOption[] = ['Commodity', 'BudgetMonth'];

    if (customerSettings.positionsByLocation) {
      groupingOptions.push('BudgetingTier');
      groupingOptions.push('Tiers');
    }

    if (customerSettings.positionsByCounterparty) {
      groupingOptions.push('Counterparty');
    }

    setGroupingOptions(groupingOptions);
  }

  function closeModal() {
    setIsConfiguringReport(false);
  }

  useEffect(updateReportRows, [positions, customerReport]);
  useEffect(updateGroupingOptions, []);

  return (
    <div>
      <StoneXRow extraHorizontalSpacing>
        { reportSettings?.inputs?.quoteDate?.show && <StoneXDatePicker onChange={setQuoteDate} label="Quote Date" value={quoteDate} width='small' /> }
        { reportSettings?.inputs?.budgetStartDate?.show && <StoneXContractMonthPicker value={budgetStartMonth} label="Budget Start Month" onChange={setBudgetStartMonth}/> }
        { reportSettings?.inputs?.budgetEndDate?.show && <StoneXContractMonthPicker value={budgetEndMonth} label="Budget End Month" onChange={setBudgetEndMonth} /> }
        
        <ButtonGroup>
          <StoneXButton variant="outlined" onClick={runReport} startIcon={<PlayArrow />} disabled={reportIsLoading}>Run</StoneXButton>
          <StoneXButton variant="outlined" onClick={downloadToExcel} startIcon={<Download />} isWorking={reportDownloadingState.isLoading()} disabled={reportIsLoading}>Excel</StoneXButton>
        </ButtonGroup>

        <Button className='pull-right' variant='outlined' onClick={() => setIsConfiguringReport(true)}>Configure Report</Button>
      </StoneXRow>

      <ScenarioMarketPrices onScenarioMaretPricesUpdated={setScenarioMarketPrices} />

      <br />

      <StoneXLoading show={reportLoadingState.isLoading()} />

      {reportLoadingState.isLoaded() && (
        <>
          <StoneXRow align='center'>
            <h2>{customerName} {customerReport.reportName}</h2>
            <PositionReportSummaryDisplay positions={positions} quoteDateString={quoteDateString!} showNetOptionPremium={reportSettings?.behavior?.netOptionPremiums ?? false} />
          </StoneXRow> 
         
          <PositionReportTable columnsToShow={columnsToShow} rows={rows} />
        </>
      )}

      <PositionReportSettingsModal open={isConfiguringReport} customerReport={customerReport} groupingOptions={groupingOptions} onClose={closeModal} onSave={closeModal} />
    </div>
  );
} 